import { useRef } from 'react';
import { GetServerSideProps } from 'next';
import { scrollToElement } from '@99designs/browser';
import { Box } from '@99designs/emu';
import { maxWidths } from '@99designs/emu/settings';
import { Site } from '@99designs/site-navigation/lib/Site';
import DefaultPageLayout from '../../components/common/DefaultPageLayout';
import AboutUs from '../../components/pages/99daysofdesign/AboutUs';
import Head from '../../components/pages/99daysofdesign/Head';
import Hero from '../../components/pages/99daysofdesign/Hero';
import OurPartners from '../../components/pages/99daysofdesign/OurPartners';
import TileGrid from '../../components/pages/99daysofdesign/TileGrid';
import { getDefaultPageProps } from '../../lib/getDefaultPageProps';
import { DAY_IN_SECONDS, HOUR_IN_SECONDS } from '../../lib/time';
import type { Role } from '@99designs/site-navigation/consts';
import type { SiteConfig } from '@99designs/site-navigation/lib/Site';

type NinetyNineDaysOfDesignPageProps = {
    siteConfig: string;
    userRole: Role;
};

export default function NinetyNineDaysOfDesignPage({
    siteConfig,
    userRole,
}: NinetyNineDaysOfDesignPageProps) {
    const tileGridRef = useRef<HTMLDivElement>(null);
    const site = new Site(JSON.parse(siteConfig) as SiteConfig);

    return (
        <DefaultPageLayout role={userRole} site={site}>
            <Head currentDomain={site.getDomain()} />
            <Box
                maxWidth={`${maxWidths.content.widescreen}px`}
                paddingX={['0.5bu', '1bu', '1bu', '1bu']}
                margin="auto"
            >
                <Hero
                    onCtaClick={() => {
                        if (tileGridRef) {
                            scrollToElement(tileGridRef.current, 20);
                        }
                    }}
                />
                <Box marginTop="3bu">
                    <OurPartners />
                </Box>
                <Box marginY="4bu">
                    <div ref={tileGridRef}>
                        <TileGrid />
                    </div>
                </Box>
                <AboutUs />
            </Box>
        </DefaultPageLayout>
    );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    const defaultProps = await getDefaultPageProps(context);

    context.res.setHeader('Surrogate-Key', 'days-of-design');
    context.res.setHeader(
        'Surrogate-Control',
        `max-age=${DAY_IN_SECONDS}, stale-while-revalidate=${HOUR_IN_SECONDS}`
    );

    return {
        props: {
            ...defaultProps,
        },
    };
};
